import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { statusPagesApi, appsApi } from '../api'
import type { StatusPageWithApp, StatusPageCreate, StatusPageUpdate, App } from '../types'
import {
  Button,
  ConfirmModal,
  Skeleton,
  Card,
  PageHeader,
  EmptyState,
  Badge,
  FormInput,
  FormLabel,
  FormField,
  FormTextarea,
} from '../components'
import { Globe, Plus, Pencil, Trash2, ExternalLink, Copy, Check, Eye, EyeOff } from 'lucide-react'

export default function StatusPagesPage() {
  const [statusPages, setStatusPages] = useState<StatusPageWithApp[]>([])
  const [apps, setApps] = useState<App[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingStatusPage, setEditingStatusPage] = useState<StatusPageWithApp | null>(null)
  const [deleteStatusPage, setDeleteStatusPage] = useState<StatusPageWithApp | null>(null)
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null)

  // Form state
  const [formData, setFormData] = useState<StatusPageCreate & StatusPageUpdate>({
    app_id: '',
    title: '',
    slug: '',
    description: '',
    logo_url: '',
    primary_color: '#3B82F6',
    background_color: '#0F172A',
    show_uptime: true,
    show_response_time: true,
    show_incident_history: true,
    incident_history_days: 90,
    is_public: true,
  })

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const [statusPagesResponse, appsResponse] = await Promise.all([
        statusPagesApi.list(),
        appsApi.list(),
      ])
      setStatusPages(statusPagesResponse.status_pages)
      setApps(appsResponse.apps)
    } catch {
      toast.error('Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  const openCreateModal = () => {
    setEditingStatusPage(null)
    setFormData({
      app_id: '',
      title: '',
      slug: '',
      description: '',
      logo_url: '',
      primary_color: '#3B82F6',
      background_color: '#0F172A',
      show_uptime: true,
      show_response_time: true,
      show_incident_history: true,
      incident_history_days: 90,
      is_public: true,
    })
    setShowModal(true)
  }

  const openEditModal = (statusPage: StatusPageWithApp) => {
    setEditingStatusPage(statusPage)
    setFormData({
      app_id: statusPage.app_id,
      title: statusPage.title,
      slug: statusPage.slug,
      description: statusPage.description || '',
      logo_url: statusPage.logo_url || '',
      primary_color: statusPage.primary_color,
      background_color: statusPage.background_color,
      show_uptime: statusPage.show_uptime,
      show_response_time: statusPage.show_response_time,
      show_incident_history: statusPage.show_incident_history,
      incident_history_days: statusPage.incident_history_days,
      is_public: statusPage.is_public,
    })
    setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      if (editingStatusPage) {
        const updateData: StatusPageUpdate = {
          title: formData.title,
          slug: formData.slug,
          description: formData.description || null,
          logo_url: formData.logo_url || null,
          primary_color: formData.primary_color,
          background_color: formData.background_color,
          show_uptime: formData.show_uptime,
          show_response_time: formData.show_response_time,
          show_incident_history: formData.show_incident_history,
          incident_history_days: formData.incident_history_days,
          is_public: formData.is_public,
        }
        await statusPagesApi.update(editingStatusPage.id, updateData)
        toast.success('Status page updated')
      } else {
        const createData: StatusPageCreate = {
          app_id: formData.app_id!,
          title: formData.title!,
          slug: formData.slug!,
          description: formData.description || undefined,
          logo_url: formData.logo_url || undefined,
          primary_color: formData.primary_color,
          background_color: formData.background_color,
          show_uptime: formData.show_uptime,
          show_response_time: formData.show_response_time,
          show_incident_history: formData.show_incident_history,
          incident_history_days: formData.incident_history_days,
          is_public: formData.is_public,
        }
        await statusPagesApi.create(createData)
        toast.success('Status page created')
      }
      setShowModal(false)
      loadData()
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } }
      toast.error(err.response?.data?.detail || 'Failed to save status page')
    }
  }

  const handleDelete = async () => {
    if (!deleteStatusPage) return
    try {
      await statusPagesApi.delete(deleteStatusPage.id)
      toast.success('Status page deleted')
      setDeleteStatusPage(null)
      loadData()
    } catch {
      toast.error('Failed to delete status page')
    }
  }

  const copySlugUrl = (slug: string) => {
    const url = `${window.location.origin}/status/${slug}`
    navigator.clipboard.writeText(url)
    setCopiedSlug(slug)
    toast.success('URL copied to clipboard')
    setTimeout(() => setCopiedSlug(null), 2000)
  }

  // Get apps that don't have a status page yet
  const availableApps = apps.filter(
    app =>
      !statusPages.some(sp => sp.app_id === app.id) ||
      (editingStatusPage && editingStatusPage.app_id === app.id)
  )

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64" />
      </div>
    )
  }

  return (
    <>
      <PageHeader
        title="Status Pages"
        subtitle="Create public status pages to share your service status"
        action={
          <Button onClick={openCreateModal} disabled={availableApps.length === 0}>
            <Plus className="w-4 h-4 mr-2" />
            Create Status Page
          </Button>
        }
      />

      {statusPages.length === 0 ? (
        <EmptyState
          icon={<Globe className="w-6 h-6" />}
          title="No status pages yet"
          description={
            availableApps.length > 0
              ? 'Create a public status page to share your service status with users'
              : 'Create a public status page to share your service status with users. Create an app first to get started.'
          }
          action={
            availableApps.length > 0
              ? {
                  label: 'Create Your First Status Page',
                  onClick: openCreateModal,
                }
              : undefined
          }
        />
      ) : (
        <div className="grid gap-4">
          {statusPages.map(statusPage => (
            <Card key={statusPage.id} className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-medium text-slate-900">{statusPage.title}</h3>
                    {statusPage.is_public ? (
                      <Badge variant="success" className="flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        Public
                      </Badge>
                    ) : (
                      <Badge className="flex items-center gap-1">
                        <EyeOff className="w-3 h-3" />
                        Private
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 mb-3">
                    App: <span className="text-slate-700">{statusPage.app_name}</span>
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="text-sm text-primary-600 bg-slate-100 px-2 py-1 rounded">
                      /status/{statusPage.slug}
                    </code>
                    <button
                      onClick={() => copySlugUrl(statusPage.slug)}
                      className="p-1 text-slate-500 hover:text-slate-900 transition-colors"
                      title="Copy URL"
                    >
                      {copiedSlug === statusPage.slug ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                    <Link
                      to={`/status/${statusPage.slug}`}
                      target="_blank"
                      className="p-1 text-slate-500 hover:text-slate-900 transition-colors"
                      title="View status page"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openEditModal(statusPage)}
                    className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setDeleteStatusPage(statusPage)}
                    className="p-2 text-slate-500 hover:text-red-600 hover:bg-slate-100 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {statusPage.description && (
                <p className="text-sm text-slate-600 mt-3">{statusPage.description}</p>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-semibold text-slate-900">
                {editingStatusPage ? 'Edit Status Page' : 'Create Status Page'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {!editingStatusPage && (
                <FormField>
                  <FormLabel>App</FormLabel>
                  <select
                    value={formData.app_id}
                    onChange={e => setFormData({ ...formData, app_id: e.target.value })}
                    required
                    className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Select an app</option>
                    {availableApps.map(app => (
                      <option key={app.id} value={app.id}>
                        {app.name}
                      </option>
                    ))}
                  </select>
                </FormField>
              )}

              <FormField>
                <FormLabel>Title</FormLabel>
                <FormInput
                  type="text"
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  required
                  placeholder="My Service Status"
                />
              </FormField>

              <FormField>
                <FormLabel>Slug</FormLabel>
                <div className="flex items-center gap-2">
                  <span className="text-slate-600">/status/</span>
                  <FormInput
                    type="text"
                    value={formData.slug}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''),
                      })
                    }
                    required
                    placeholder="my-service"
                    className="flex-1"
                  />
                </div>
              </FormField>

              <FormField>
                <FormLabel>Description</FormLabel>
                <FormTextarea
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Service status and uptime information"
                  rows={2}
                />
              </FormField>

              <div className="grid grid-cols-2 gap-4">
                <FormField>
                  <FormLabel>Primary Color</FormLabel>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.primary_color}
                      onChange={e => setFormData({ ...formData, primary_color: e.target.value })}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <FormInput
                      type="text"
                      value={formData.primary_color}
                      onChange={e => setFormData({ ...formData, primary_color: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </FormField>
                <FormField>
                  <FormLabel>Background Color</FormLabel>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.background_color}
                      onChange={e => setFormData({ ...formData, background_color: e.target.value })}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <FormInput
                      type="text"
                      value={formData.background_color}
                      onChange={e => setFormData({ ...formData, background_color: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                </FormField>
              </div>

              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.show_uptime}
                    onChange={e => setFormData({ ...formData, show_uptime: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-300 text-primary-500 focus:ring-primary-500"
                  />
                  <span className="text-sm text-slate-700">Show uptime statistics</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.show_response_time}
                    onChange={e =>
                      setFormData({ ...formData, show_response_time: e.target.checked })
                    }
                    className="w-4 h-4 rounded border-slate-300 text-primary-500 focus:ring-primary-500"
                  />
                  <span className="text-sm text-slate-700">Show response time chart</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.show_incident_history}
                    onChange={e =>
                      setFormData({ ...formData, show_incident_history: e.target.checked })
                    }
                    className="w-4 h-4 rounded border-slate-300 text-primary-500 focus:ring-primary-500"
                  />
                  <span className="text-sm text-slate-700">Show incident history</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_public}
                    onChange={e => setFormData({ ...formData, is_public: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-300 text-primary-500 focus:ring-primary-500"
                  />
                  <span className="text-sm text-slate-700">Make page public</span>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="ghost" type="button" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingStatusPage ? 'Save Changes' : 'Create Status Page'}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteStatusPage}
        onClose={() => setDeleteStatusPage(null)}
        onConfirm={handleDelete}
        title="Delete Status Page"
        message={`Are you sure you want to delete the status page "${deleteStatusPage?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />
    </>
  )
}
